import { type FC, type ReactNode, Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { graphql, useFragment, useQueryLoader } from 'react-relay';
import PdpSharedShippingInfoLoading from './PdpSharedShippingInfoLoading';
import { getAbTestV2 } from 'dibs-ab-tests/exports/clientAbTestV2';
import { usePdpReturnsClientVariant } from '../hooks/usePdpReturnsClientVariant';
import { PdpSharedShippingAndReturns } from '../shared/PdpSharedShippingAndReturns/PdpSharedShippingAndReturns';

import {
    getUserGeoInfo,
    WAIT_FOR_SHIPPING_REGIONS,
} from 'dibs-regional-info/exports/regionalInfoHelpers';
import { useUserShippingRegionsUrlLabels } from '../PdpSharedLowerShippingInfo/hooks/useUserShippingRegionsUrlLabels';
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions';

import { type PdpSharedShippingInfoWrapper_item$key } from './__generated__/PdpSharedShippingInfoWrapper_item.graphql';
import { type PdpSharedShippingInfoWrapper_sku$key } from './__generated__/PdpSharedShippingInfoWrapper_sku.graphql';
import { type PdpSharedShippingInfoWrapper_user$key } from './__generated__/PdpSharedShippingInfoWrapper_user.graphql';

import {
    type PdpSharedShippingInfoWrapperQuery as PdpSharedShippingInfoWrapperQueryType,
    type PdpSharedShippingInfoWrapperQuery$variables,
} from './__generated__/PdpSharedShippingInfoWrapperQuery.graphql';

const PdpSharedShippingInfoSidebarComponent = lazy(() =>
    import(
        /* webpackChunkName: "PdpSharedShippingInfoSidebarComponent" */ './PdpSharedShippingInfoSidebarComponent'
    ).then(mod => ({ default: mod.PdpSharedShippingInfoSidebarComponent }))
);

const PdpSharedShippingInfoComponent = lazy(() =>
    import(
        /* webpackChunkName: "PdpSharedShippingInfoComponent" */ './PdpSharedShippingInfoComponent'
    ).then(mod => ({ default: mod.PdpSharedShippingInfoComponent }))
);

//In this case we won't satisfy the eslint rule since the child component is using theses fields
/* eslint-disable relay/unused-fields */
export const PdpSharedShippingInfoWrapperQuery = graphql`
    query PdpSharedShippingInfoWrapperQuery(
        $itemId: String!
        $userId: String!
        $fetchRegionalInfo: Boolean = false
        $userZipCode: String = ""
        $userCountryCode: String = ""
        $isTrade: Boolean = false
        $fetchItem: Boolean = false
        $fetchUser: Boolean = false
        $afterDisplay: Boolean = false
        $itemQuantity: Int
        $shippingRegions: [String] = []
        $mlFreightQuotesv2TestArm: String
    ) {
        viewer {
            ...PdpSharedShippingInfoLink_viewer
            ...PdpSharedPromoCode_viewer
            ...PdpSharedShippingAddress_viewer
            ...PdpSharedLowerShippingInfo_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
            ...useShippingPrice_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
            ...useShippingType_viewer
                @arguments(
                    userId: $userId
                    itemId: $itemId
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                    itemQuantity: $itemQuantity
                )
            regionSelectorEnabled: featureFlag(feature: "pdpRegionSelector")
            geography {
                countries {
                    name
                    abbreviation
                }
            }
            relevantShipping(
                itemId: $itemId
                buyerId: $userId
                quantity: $itemQuantity
                zipCode: $userZipCode
                countryCode: $userCountryCode
                mlFreightQuotesv2TestArm: $mlFreightQuotesv2TestArm
            ) {
                quoteRequestStatus
                mlFreightQuotesv2Eligible
                shipmentQuote {
                    ...PdpSharedShippingInfoLink_shipmentQuote
                    ...TrackQuoteShown_shipmentQuote
                    type
                    handlingTimeDisplay(displayShort: true)
                    handlingTimeDisplayUnformatted {
                        min
                        max
                        unit
                    }
                    subTotalAmount {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    buyerServiceMethodDisplay
                    showQuoteAsPending
                    serviceMethod {
                        serviceType
                        serviceMethodType
                    }
                }
                shipment {
                    shippingAddress {
                        displayCityStateCountry
                    }
                }
            }
            itemRead(itemIds: [$itemId]) @include(if: $fetchItem) {
                ... on Item {
                    ...PdpSharedLowerShippingInfo_item
                        @arguments(
                            isTrade: $isTrade
                            buyerId: $userId
                            fetchShippingCostInfo: true
                            itemQuantity: $itemQuantity
                            userCountryCode: $userCountryCode
                            userZipCode: $userZipCode
                            shippingRegions: $shippingRegions
                            mlFreightQuotesv2TestArm: $mlFreightQuotesv2TestArm
                        )
                    ...useShippingPrice_item
                        @arguments(
                            buyerId: $userId
                            fetchShippingCostInfo: true
                            itemQuantity: $itemQuantity
                            userZipCode: $userZipCode
                            userCountryCode: $userCountryCode
                            mlFreightQuotesv2TestArm: $mlFreightQuotesv2TestArm
                        )

                    ...useShippingType_item
                        @arguments(
                            userId: $userId
                            itemQuantity: $itemQuantity
                            userZipCode: $userZipCode
                            userCountryCode: $userCountryCode
                            mlFreightQuotesv2TestArm: $mlFreightQuotesv2TestArm
                        )
                    ...PdpSharedShippingInfoLink_item
                    ...PdpSharedShippingInfoWrapper_item
                    ...PdpSharedDutiesMessage_item
                    serviceId
                    isItemRTQuoteEligibly(
                        buyerId: $userId
                        countryCode: $userCountryCode
                        zipCode: $userZipCode
                    )
                    vertical
                    address {
                        displayCountry
                        country
                    }
                    ...PdpSharedShippingAndReturns_item
                    ...PdpSharedReturns_item
                    ...useShowReturns_item
                }
            }
            user(userId: $userId) @include(if: $fetchUser) {
                ...PdpSharedPromoCode_user
                ...PdpSharedShippingInfoLink_user
                ...PdpSharedShippingAddress_user
                ...useUserGeoInfo_user
                ...PdpSharedReturns_user
                serviceId
                addressBook {
                    proAddress {
                        displayCountry
                        country
                        zipCode
                    }
                    personalAddress {
                        displayCountry
                        country
                        zipCode
                    }
                    addressList {
                        displayCountry
                        country
                        zipCode
                    }
                }
            }
        }
    }
`;
/* eslint-disable relay/unused-fields */

type Props = {
    item: PdpSharedShippingInfoWrapper_item$key;
    user: PdpSharedShippingInfoWrapper_user$key | null | undefined;
    currency: string;
    children?: ReactNode;
    leadTime?: string | null;
    renderTooltip?: boolean;
    isClient?: boolean;
    title?: ReactNode;
    displayInline?: boolean;
    preLinkQuoteDetails?: ReactNode;
    postLinkQuoteDetails?: ReactNode;
    postLinkNoQuoteDetails?: ReactNode;
    itemQuantity?: number | null;
    calculateLowerShippingInfo?: boolean;
    positioning?: 'sidebar' | 'seller_details';
    showShippingIcon?: boolean;
    shortLinkCta?: boolean;
    shortRequestQuote?: boolean;
    shippingInfoClass?: string;
    sku?: PdpSharedShippingInfoWrapper_sku$key | null | undefined;
};

const PdpSharedShippingInfoWrapper: FC<Props> = ({
    item: itemRef,
    user: userRef,
    currency,
    children,
    leadTime,
    renderTooltip,
    isClient = false,
    title,
    displayInline,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    itemQuantity,
    calculateLowerShippingInfo = false,
    positioning,
    showShippingIcon,
    shippingInfoClass,
    shortLinkCta,
    shortRequestQuote,
    sku: skuRef,
}) => {
    const [queryReference, loadQuery] = useQueryLoader<PdpSharedShippingInfoWrapperQueryType>(
        PdpSharedShippingInfoWrapperQuery
    );
    const dispatch = useDispatch();
    const shippingRegions = useUserShippingRegionsUrlLabels();
    const [showSpinner, setShowSpinner] = useState(true);

    const user = useFragment(
        graphql`
            fragment PdpSharedShippingInfoWrapper_user on User {
                serviceId
                isVerifiedTrade
            }
        `,
        userRef
    );
    const item = useFragment(
        graphql`
            fragment PdpSharedShippingInfoWrapper_item on Item {
                serviceId
                ...PdpSharedShippingAndReturns_item
            }
        `,
        itemRef
    );
    const sku = useFragment(
        graphql`
            fragment PdpSharedShippingInfoWrapper_sku on ItemSkuType {
                serviceId
            }
        `,
        skuRef
    );

    const itemId = item?.serviceId;
    const skuId = sku?.serviceId || itemId || '';
    const userId = user?.serviceId;
    const isTradeUser = user?.isVerifiedTrade;

    const reloadQuery = useCallback(
        (variables: Partial<PdpSharedShippingInfoWrapperQuery$variables>) => {
            /**
             * Wait for shipping regions to be populated (either an array or undefined)
             * By doing this we avoid unnecessary shipmentQuotes/live calls
             */
            if (shippingRegions !== WAIT_FOR_SHIPPING_REGIONS) {
                const userGeoInfo = getUserGeoInfo();
                if (typeof window !== 'undefined') {
                    (window as $TSFixMe).__DIBS_QUOTE_TIMING__ = Date.now();
                }
                loadQuery(
                    {
                        itemId: itemId || '',
                        userId: userId || '',
                        isTrade: isTradeUser,
                        fetchUser: !!userId,
                        fetchItem: !!itemId,
                        afterDisplay: isClient,
                        itemQuantity,
                        userCountryCode: userGeoInfo?.countryCode || '',
                        userZipCode: userGeoInfo?.zipCode || '',
                        shippingRegions,
                        mlFreightQuotesv2TestArm: getAbTestV2('mlFreightQuotesv2')?.variant,
                        // We want to override query variables with the new ones when refetching the query
                        ...variables,
                    },
                    { fetchPolicy: 'network-only' }
                );
                dispatch(
                    updateRelayVariables({
                        updateItemPricing: true,
                        updatedUserCountryCode: variables?.userCountryCode,
                    })
                );
            }
        },
        [loadQuery, dispatch, itemId, userId, isTradeUser, isClient, itemQuantity, shippingRegions]
    );

    useEffect(() => {
        /**
         * Wait for shipping regions to be populated (either an array or undefined)
         * By doing this we avoid unnecessary shipmentQuotes/live calls
         */
        if (isClient && shippingRegions !== WAIT_FOR_SHIPPING_REGIONS) {
            const userGeoInfo = getUserGeoInfo();
            if (typeof window !== 'undefined') {
                (window as $TSFixMe).__DIBS_QUOTE_TIMING__ = Date.now();
            }
            loadQuery(
                {
                    itemId: itemId || '',
                    userId: userId || '',
                    isTrade: isTradeUser,
                    fetchUser: !!userId,
                    fetchItem: !!itemId,
                    afterDisplay: isClient,
                    userCountryCode: userGeoInfo?.countryCode || '',
                    userZipCode: userGeoInfo?.zipCode || '',
                    itemQuantity,
                    shippingRegions,
                    fetchRegionalInfo: false,
                    mlFreightQuotesv2TestArm: getAbTestV2('mlFreightQuotesv2')?.variant,
                },
                { fetchPolicy: 'network-only' }
            );
        }
    }, [loadQuery, isClient, itemId, userId, isTradeUser, itemQuantity, shippingRegions]);

    useEffect(() => {
        if (showSpinner) {
            const timer = setTimeout(() => setShowSpinner(false), 2000);
            return () => clearTimeout(timer);
        }
        return () => {};
    }, [showSpinner]);

    const isPdpReturnsClientVariant = usePdpReturnsClientVariant();

    const loading =
        isPdpReturnsClientVariant && positioning === 'sidebar' ? (
            <PdpSharedShippingAndReturns item={item}>
                <PdpSharedShippingInfoLoading title="" />
            </PdpSharedShippingAndReturns>
        ) : (
            <PdpSharedShippingInfoLoading title={title}>{children}</PdpSharedShippingInfoLoading>
        );

    if (!queryReference || showSpinner) {
        return loading;
    }

    return (
        <Suspense fallback={loading}>
            {positioning === 'sidebar' ? (
                <PdpSharedShippingInfoSidebarComponent
                    queryReference={queryReference}
                    reloadQuery={reloadQuery}
                    currency={currency}
                    preLinkQuoteDetails={preLinkQuoteDetails}
                    postLinkQuoteDetails={postLinkQuoteDetails}
                    postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                    itemQuantity={itemQuantity}
                    showShippingIcon={showShippingIcon}
                    shippingInfoClass={shippingInfoClass}
                    shortLinkCta={shortLinkCta}
                    shortRequestQuote={shortRequestQuote}
                    skuId={skuId}
                >
                    {children}
                </PdpSharedShippingInfoSidebarComponent>
            ) : (
                <PdpSharedShippingInfoComponent
                    queryReference={queryReference}
                    reloadQuery={reloadQuery}
                    renderTooltip={renderTooltip}
                    currency={currency}
                    leadTime={leadTime || ''}
                    userId={user?.serviceId || ''}
                    title={title}
                    displayInline={displayInline}
                    preLinkQuoteDetails={preLinkQuoteDetails}
                    postLinkQuoteDetails={postLinkQuoteDetails}
                    postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                    itemQuantity={itemQuantity}
                    calculateLowerShippingInfo={calculateLowerShippingInfo}
                    skuId={skuId}
                >
                    {children}
                </PdpSharedShippingInfoComponent>
            )}
        </Suspense>
    );
};

export default PdpSharedShippingInfoWrapper;
